import classnames from "classnames";
import React, { useContext } from "react";
import { EducationType, RefType, ValidationType } from "../../types";
import { StoreContext } from "../context/Store";
import Button from "../parts/Button";
import ElementsList from "../parts/ElementsList";
import Input from "../parts/Input";
import Text from "../parts/Text";
import {
  formValidation,
  scrollTo,
  validateLength,
  validateYear,
} from "../utils";

export interface EducationProps {
  scrollTo: RefType;
}

export interface EducationState {}

const validation: ValidationType<EducationType> = {
  name: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
  address: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
  title: {
    valid: (value: string) => validateLength(value),
    required: false,
  },
  profile: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
  year: {
    valid: (year: string) => validateYear(year),
    required: true,
  },
};

export const Education = React.forwardRef<HTMLDivElement, EducationProps>(
  (props, ref) => {
    const store = useContext(StoreContext);
    const {
      tempEducation,
      editEducation,
      removeEducation,
      setTempEducation,
      addEducation,
      education,
      educationEditIndex,
    } = store;
    const { name, title, profile, year } = tempEducation;

    const change = (e: React.ChangeEvent<HTMLInputElement>) => {
      setTempEducation(e.target.name as keyof EducationType, e.target.value);
    };

    const goNext = () => {
      scrollTo(props.scrollTo);
    };

    const renderElement = (item: EducationType) => {
      return (
        <>
          <Text tag="h5" color="blue">
            {item.year}
          </Text>
          <Text tag="p" classes="headline">
            {item.name}
          </Text>
          <Text tag="p" classes="subheadline">
            {item.title ? `${item.title} |` : ""}&nbsp;
            {item.profile}
          </Text>
        </>
      );
    };

    return (
      <section
        id="education"
        ref={ref}
        className={classnames("mb-5", { blink: educationEditIndex !== null })}
      >
        <Text tag="h2" color="pink" classes="mb-5">
          Wykształcenie
        </Text>
        <Text tag="p">W tej części pewnie wpiszesz najwięcej.</Text>
        <Text tag="h4" classes="mb-2" color="blue">
          Podaj nazwy szkół, w których się uczyłeś.
        </Text>
        <Text tag="p" classes="mb-5">
          W przypadku studiów warto wpisać też nazwę kierunku, specjalności i
          tryb studiów.
        </Text>
        <Text tag="h4" classes="mb-2" color="blue">
          Podaj daty rozpoczęcia i zakończenia kolejnych etapów edukacji.
        </Text>
        <Text tag="p" classes="mb-5">
          Nie muszą być dokładne co do dnia, miesiąc i rok wystarczą.
        </Text>
        <div className="mb-5">
          <div className="row">
            <div className="col-12 col-sm-6">
              <Input
                name="name"
                label="Nazwa szkoły"
                onChange={change}
                value={name}
                required={validation.name.required}
                isValid={validation.name.valid(name)}
                id="schoolName"
              />
            </div>
            <div className="col-12 col-sm-6">
              <Input
                name="title"
                label="Stopień naukowy / zawód (dla szkół zawodowych)"
                onChange={change}
                value={title}
                required={validation.title.required}
                isValid={validation.title.valid(title)}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-sm-8">
              <Input
                name="profile"
                label="Kierunek studiów / profil klasy"
                onChange={change}
                value={profile}
                required={validation.profile.required}
                isValid={validation.profile.valid(profile)}
              />
            </div>
            <div className="col-12 col-sm-4">
              <Input
                name="year"
                label="Rok ukończenia"
                onChange={change}
                value={year}
                required={validation.year.required}
                isValid={validation.year.valid(year)}
                type="number"
              />
            </div>
          </div>
        </div>
        {education.length > 0 && (
          <div className="mt-5">
            <ElementsList
              data={education}
              onEdit={editEducation}
              onRemove={removeEducation}
              renderElement={renderElement}
              editIndex={educationEditIndex}
            />
          </div>
        )}
        <div className="row mb-5">
          <div className="col-12 col-xl-6">
            <Button
              onClick={addEducation}
              classes="w-100 smaller-text smaller-padding pink-border mb-2"
              disabled={
                !formValidation<EducationType>(validation, tempEducation)
              }
            >
              {educationEditIndex !== null
                ? "aktualizuj"
                : "dodaj wykształcenie"}
            </Button>
          </div>
          <div className="col-12 col-xl-6">
            <Button
              onClick={goNext}
              classes="pink w-100 smaller-text smaller-padding mb-2"
            >
              dalej
            </Button>
          </div>
        </div>
      </section>
    );
  }
);

export default Education;
